import { config } from "@caixa/config";
import type { ItemCarrinho } from "@caixa/types";

const BASE = `${config.BASE_URL.replace(/\/$/, "")}`;
const defaultOptions: RequestInit = {
  credentials: config.credentials,
  headers: { "Content-Type": "application/json" },
};

export interface ComandaAberta {
  id: number;
  nome: string;
  total: number;
  createdAt: string;
  updatedAt: string;
  cliente_nome?: string | null;
  cliente_whatsapp?: string | null;
  origem?: string | null;
  numero_comanda?: string | null;
  numero_mesa?: string | null;
}

export interface ComandaItem {
  id: number;
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
  observacao?: string | null;
  cozinha_pendente?: boolean;
  entrada_id?: string;
  desconto?: number;
  promocao_id?: number | null;
  itens_escolhidos?: ItemCarrinho['itens_escolhidos'];
  entradas?: Array<{
    entrada_id: string; quantidade: number; preco_unitario: number; desconto: number;
    observacao?: string; promocao_id?: number | null; itens_escolhidos?: ItemCarrinho['itens_escolhidos'];
  }> | null;
}

/** Expand only known persisted portions; a legacy consolidated row stays a single entry. */
export function expandComandaItens(itens: ComandaItem[]): ComandaItem[] {
  return itens.flatMap((item) => {
    if (!item.entradas?.length) return [item];
    const sum = item.entradas.reduce((total, entry) => total + Math.round(entry.quantidade * 1000), 0);
    if (sum !== Math.round(item.quantidade * 1000)) return [item];
    return item.entradas.map((entry) => ({
      ...item, ...entry, entradas: undefined,
      subtotal: entry.quantidade * entry.preco_unitario - entry.desconto,
    }));
  });
}

type ComandaSalvarItem = {
  id?: string; permiteDecimal?: boolean; cod: number; nome: string; quantidade: number;
  precoUnitario: number; desconto?: number; observacao?: string; promocao_id?: number;
  itens_escolhidos?: ItemCarrinho['itens_escolhidos'];
};

function serializeComandaItem(i: ComandaSalvarItem) {
  return {
    produto_id: i.cod, nome_produto: i.nome, quantidade: i.quantidade, preco_unitario: i.precoUnitario,
    subtotal: i.quantidade * i.precoUnitario - (i.desconto ?? 0),
    ...(i.observacao?.trim() ? { observacao: i.observacao.trim() } : {}),
    ...(i.permiteDecimal && i.id ? {
      entrada_id: i.id, desconto: i.desconto ?? 0, promocao_id: i.promocao_id,
      itens_escolhidos: i.itens_escolhidos,
    } : {}),
  };
}

export interface ComandaComItens extends ComandaAberta {
  itens: ComandaItem[];
}

async function handleJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  const trimmed = text.trim();

  if (!trimmed) {
    if (res.ok) return {} as T;
    throw new Error("Erro na requisição");
  }

  let data: unknown;
  try {
    data = JSON.parse(trimmed);
  } catch {
    if (!res.ok) {
      throw new Error(trimmed.slice(0, 200) || "Erro na requisição");
    }
    throw new Error("Resposta inválida do servidor");
  }

  const obj = data && typeof data === "object" ? (data as Record<string, unknown>) : null;
  const serverMsg =
    obj && typeof obj.error === "string" && obj.error.trim() !== ""
      ? obj.error.trim()
      : obj && typeof obj.message === "string" && obj.message.trim() !== ""
        ? obj.message.trim()
        : "";

  if (!res.ok || (obj && obj.ok === false)) {
    throw new Error(serverMsg || "Erro na requisição");
  }

  return data as T;
}

/** Itens da comanda fechada no /garcom para finalizar no Caixa. */
export const GARCOM_FECHAR_COMANDA_STORAGE_KEY = "garcom_fechar_comanda";

export async function listComandas(options?: { signal?: AbortSignal }): Promise<ComandaAberta[]> {
  const res = await fetch(`${BASE}/comandas/list.php`, {
    ...defaultOptions,
    method: "GET",
    cache: "no-store",
    signal: options?.signal,
  });
  const data = await handleJson<{ comandas: ComandaAberta[] }>(res);
  return data.comandas ?? [];
}

export async function findComandaAbertaPorMesa(
  mesa: string,
  options?: { signal?: AbortSignal },
): Promise<ComandaAberta | null> {
  const normalized = mesa.trim();
  if (!normalized) return null;
  const res = await fetch(
    `${BASE}/comandas/find-aberta-por-mesa.php?mesa=${encodeURIComponent(normalized)}`,
    {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    },
  );
  const data = await handleJson<{ comanda: ComandaAberta | null }>(res);
  return data.comanda ?? null;
}

export async function findComandaAbertaPorIdentificacao(
  identificacao: string,
  options?: { signal?: AbortSignal },
): Promise<ComandaAberta | null> {
  const normalized = identificacao.trim();
  if (!normalized) return null;
  const res = await fetch(
    `${BASE}/comandas/find-aberta-por-mesa.php?identificacao=${encodeURIComponent(normalized)}`,
    {
      ...defaultOptions,
      method: "GET",
      signal: options?.signal,
    },
  );
  const data = await handleJson<{ comanda: ComandaAberta | null }>(res);
  return data.comanda ?? null;
}

export async function lookupComandasPorIdentificacao(identificacao: string, options?: { signal?: AbortSignal }): Promise<{
  comanda: ComandaAberta | null; candidatas: ComandaAberta[]; requer_selecao: boolean;
}> {
  const res = await fetch(`${BASE}/comandas/find-aberta-por-mesa.php?identificacao=${encodeURIComponent(identificacao.trim())}`, {
    ...defaultOptions, method: "GET", signal: options?.signal,
  });
  const data = await handleJson<{ comanda: ComandaAberta | null; candidatas?: ComandaAberta[]; requer_selecao?: boolean }>(res);
  return { comanda: data.comanda, candidatas: data.candidatas ?? [], requer_selecao: data.requer_selecao === true };
}

export async function createComanda(params: {
  nome: string;
  comandaId?: number;
  itens: ComandaSalvarItem[];
  total: number;
  enviarCozinha?: boolean;
  notificarGarcom?: boolean;
  identificacaoLivre?: boolean;
}): Promise<{ comanda_id: number; action: "created" | "added" }> {
  const itens = params.itens.map(serializeComandaItem);
  const res = await fetch(`${BASE}/comandas/create.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({
      nome: params.nome.trim(),
      ...(params.comandaId ? { comanda_id: params.comandaId } : {}),
      itens,
      total: params.total,
      enviar_cozinha: params.enviarCozinha !== false,
      notificar_garcom: params.notificarGarcom !== false,
      identificacao_livre: params.identificacaoLivre !== false,
    }),
  });
  const data = await handleJson<{
    comanda_id: number;
    action?: string;
    comanda?: { id: number };
  }>(res);
  const comandaId = data.comanda_id ?? data.comanda?.id;
  if (!comandaId || comandaId <= 0) {
    throw new Error("Resposta inválida ao criar comanda.");
  }
  const action = data.action === "added" ? "added" : "created";
  return { comanda_id: comandaId, action };
}

export async function getComanda(id: number, options?: { signal?: AbortSignal }): Promise<ComandaComItens> {
  const res = await fetch(`${BASE}/comandas/get.php?id=${encodeURIComponent(String(id))}`, {
    ...defaultOptions,
    method: "GET",
    signal: options?.signal,
  });
  const data = await handleJson<{ comanda: ComandaComItens }>(res);
  return data.comanda;
}

export async function closeComanda(id: number): Promise<{
  itens: ComandaItem[];
  ja_enviado_cozinha: boolean;
  nome?: string;
  comanda_id?: number;
}> {
  const res = await fetch(`${BASE}/comandas/close.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({ id }),
  });
  const data = await handleJson<{
    itens: ComandaItem[];
    ja_enviado_cozinha?: boolean;
    nome?: string;
    comanda_id?: number;
  }>(res);
  return {
    itens: data.itens ?? [],
    ja_enviado_cozinha: data.ja_enviado_cozinha === true,
    nome: typeof data.nome === "string" ? data.nome : undefined,
    comanda_id: typeof data.comanda_id === "number" ? data.comanda_id : undefined,
  };
}

export async function deleteComanda(id: number): Promise<void> {
  const res = await fetch(`${BASE}/comandas/delete.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({ id }),
  });
  await handleJson(res);
}

export async function updateComanda(params: {
  id: number;
  itens: ComandaSalvarItem[];
  total: number;
  enviarCozinha?: boolean;
  notificarGarcom?: boolean;
  identificacaoLivre?: boolean;
}): Promise<void> {
  const itens = params.itens.map(serializeComandaItem);
  const res = await fetch(`${BASE}/comandas/update.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({
      id: params.id,
      itens,
      total: params.total,
      enviar_cozinha: params.enviarCozinha !== false,
      notificar_garcom: params.notificarGarcom !== false,
      identificacao_livre: params.identificacaoLivre !== false,
    }),
  });
  await handleJson(res);
}

/**
 * Remove itens da Mesa/Comanda já fechada financeiramente. Quando solicitado,
 * também baixa somente as pendências correspondentes no KDS.
 */
export async function cancelarItensCozinha(
  comandaId: number,
  itemIds: number[],
  sincronizarCozinha = true,
): Promise<void> {
  if (itemIds.length === 0) return;
  const res = await fetch(`${BASE}/comandas/cozinha/cancelar-itens.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({
      comanda_id: comandaId,
      item_ids: itemIds,
      sincronizar_cozinha: sincronizarCozinha,
    }),
  });
  await handleJson(res);
}

export async function alterarMesaComanda(params: {
  id: number;
  mesa: string;
  identificacaoLivre?: boolean;
}): Promise<{ comanda_id: number; nome: string; action: string; total: number }> {
  const res = await fetch(`${BASE}/comandas/alterar-mesa.php`, {
    ...defaultOptions,
    method: "POST",
    body: JSON.stringify({
      id: params.id,
      mesa: params.mesa.trim(),
      identificacao_livre: params.identificacaoLivre !== false,
    }),
  });
  const data = await handleJson<{
    comanda_id: number;
    nome: string;
    action?: string;
    total?: number;
    comanda?: { id: number; nome: string; total: number };
  }>(res);
  const comandaId = data.comanda_id ?? data.comanda?.id;
  const nome = data.nome ?? data.comanda?.nome;
  if (!comandaId || comandaId <= 0 || !nome) {
    throw new Error("Resposta inválida ao alterar comanda.");
  }
  return {
    comanda_id: comandaId,
    nome,
    action: typeof data.action === "string" ? data.action : "renamed",
    total: typeof data.total === "number" ? data.total : (data.comanda?.total ?? 0),
  };
}
